import React, {useMemo, useState} from 'react';

import {useGate, useStore} from 'effector-react';
import {saveAs} from 'file-saver';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

import {exportRecallAuditEvent} from 'api/recallAudit';
import {ReactComponent as UploadIcon} from 'assets/svg/download.svg';
import {BackTo} from 'components/common/BackTo';
import {Page} from 'components/common/Page';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {Spinner} from 'components/ui-kit/Spinner';
import {ROUTE} from 'core/routes';
import {profile$} from 'models/auth';
import {detailState$, DetailGate} from 'models/recallAudit';
import {UserRoles} from 'types/auth';

import {
    CountRow,
    CountsTable,
    DetailCard,
    DetailHeader,
    DetailHeaderActions,
    DetailId,
    DetailTitle,
    EmptyDetail,
    Field,
    FieldLabel,
    FieldsGrid,
    FieldValue,
    Section,
    SectionTitle,
    StatusesGrid,
    StatusItem,
} from './styled';
import {
    formatLocalDateTime,
    mapAffectedOrganizations,
    mapPreviousStatuses,
    translateUserRole,
} from './utils';

const STATUS_COLORS = ['#55cc52', '#c5a400', '#6466e9', '#ff9500', '#59606a'];
const MONO_FIELDS = new Set([
    'productCode',
    'batchNumber',
    'batchId',
    'reasonCode',
    'regulatorDocumentNumber',
]);

export const RecallAuditDetailPage = () => {
    const {t} = useTranslation();
    const {eventId} = useParams<{eventId: string}>();
    useGate(DetailGate, {eventId});

    const profile = useStore(profile$);
    const {data: entry, isLoading} = useStore(detailState$);
    const [exportPending, setExportPending] = useState(false);

    const organizations = useMemo(() => mapAffectedOrganizations(entry?.affectedOrganizations), [
        entry?.affectedOrganizations,
    ]);
    const statuses = useMemo(() => mapPreviousStatuses(entry?.previousStatuses), [
        entry?.previousStatuses,
    ]);
    const canExport = profile?.roles.some((role) =>
        [UserRoles.ROLE_MPL_OPERATOR_ADMIN, UserRoles.ROLE_MPL_TS_DIRECTOR].includes(role),
    );

    const exportPdf = async () => {
        if (!entry) return;

        try {
            setExportPending(true);
            const data = await exportRecallAuditEvent(entry.eventId);
            saveAs(data, `recall-audit-${entry.eventId}.pdf`);
        } finally {
            setExportPending(false);
        }
    };

    const fields = entry
        ? ([
              ['productCode', entry.productCode],
              ['batchNumber', entry.batchNumber],
              ['batchId', entry.batchId],
              ['productionDate', formatLocalDateTime(entry.productionDate, true)],
              ['reasonCode', entry.reasonCode],
              ['reason', entry.reason],
              ['recallDecisionDate', formatLocalDateTime(entry.recallDecisionDate, true)],
              ['regulatorDocumentNumber', entry.regulatorDocumentNumber],
              ['affectedCodesCount', entry.affectedCodesCount],
              ['comment', entry.comment],
          ] as Array<[string, unknown]>).filter(
              ([, value]) => value !== undefined && value !== null && value !== '-',
          )
        : [];

    return (
        <Page title={t('pages.recallAudit.detail.title')}>
            <BackTo to={ROUTE.RECALL_AUDIT}>{t('pages.recallAudit.detail.back')}</BackTo>
            {isLoading ? (
                <EmptyDetail>
                    <Spinner />
                </EmptyDetail>
            ) : !entry ? (
                <EmptyDetail>{t('pages.recallAudit.detail.notFound')}</EmptyDetail>
            ) : (
                <DetailCard>
                    <DetailHeader>
                        <div>
                            <DetailTitle>{t('pages.recallAudit.detail.title')}</DetailTitle>
                            <DetailId>ID: {entry.eventId}</DetailId>
                        </div>
                        <DetailHeaderActions>
                            {canExport && (
                                <Button
                                    size="sm"
                                    variant={ButtonVariant.Secondary}
                                    isLoading={exportPending}
                                    onClick={exportPdf}
                                    icon={<Icon source={UploadIcon} width={16} height={16} />}
                                >
                                    {t('pages.recallAudit.export.pdf')}
                                </Button>
                            )}
                        </DetailHeaderActions>
                    </DetailHeader>

                    <Section>
                        <SectionTitle>{t('pages.recallAudit.detail.general')}</SectionTitle>
                        <FieldsGrid>
                            <Field>
                                <FieldLabel>{t('pages.recallAudit.detail.dateTime')}</FieldLabel>
                                <FieldValue>{formatLocalDateTime(entry.eventTimestamp)}</FieldValue>
                            </Field>
                            <Field>
                                <FieldLabel>{t('pages.recallAudit.detail.userId')}</FieldLabel>
                                <FieldValue $mono>{entry.userId || '-'}</FieldValue>
                            </Field>
                            <Field>
                                <FieldLabel>{t('pages.recallAudit.detail.userRole')}</FieldLabel>
                                <FieldValue>{translateUserRole(t, entry.userRole)}</FieldValue>
                            </Field>
                            <Field>
                                <FieldLabel>{t('pages.recallAudit.detail.fullName')}</FieldLabel>
                                <FieldValue>{entry.userFullName || '-'}</FieldValue>
                            </Field>
                            <Field>
                                <FieldLabel>{t('pages.recallAudit.detail.ipAddress')}</FieldLabel>
                                <FieldValue $mono>{entry.ipAddress || '-'}</FieldValue>
                            </Field>
                            <Field wide>
                                <FieldLabel>{t('pages.recallAudit.detail.userAgent')}</FieldLabel>
                                <FieldValue $mono>{entry.userAgent || '-'}</FieldValue>
                            </Field>
                        </FieldsGrid>
                    </Section>

                    {!!fields.length && (
                        <Section>
                            <SectionTitle>
                                {t('pages.recallAudit.detail.recallDetails')}
                            </SectionTitle>
                            <FieldsGrid>
                                {fields.map(([key, value]) => (
                                    <Field key={String(key)} wide={key === 'comment'}>
                                        <FieldLabel>
                                            {t(`pages.recallAudit.detail.${String(key)}`)}
                                        </FieldLabel>
                                        <FieldValue $mono={MONO_FIELDS.has(key)}>
                                            {String(value)}
                                        </FieldValue>
                                    </Field>
                                ))}
                            </FieldsGrid>
                        </Section>
                    )}

                    {!!organizations.length && (
                        <Section>
                            <SectionTitle>
                                {t('pages.recallAudit.detail.affectedOrganizations')}
                            </SectionTitle>
                            <CountsTable>
                                <CountRow header>
                                    <span>{t('pages.recallAudit.detail.organizationId')}</span>
                                    <span>{t('pages.recallAudit.detail.codesWrittenOff')}</span>
                                </CountRow>
                                {organizations.map((item, index) => (
                                    <CountRow key={`${item.label}-${index}`}>
                                        <span>{item.label}</span>
                                        <strong>{item.count ?? '-'}</strong>
                                    </CountRow>
                                ))}
                            </CountsTable>
                        </Section>
                    )}

                    {!!statuses.length && (
                        <Section>
                            <SectionTitle>
                                {t('pages.recallAudit.detail.previousStatuses')}
                            </SectionTitle>
                            <StatusesGrid>
                                {statuses.map((item, index) => (
                                    <StatusItem
                                        key={`${item.label}-${index}`}
                                        color={STATUS_COLORS[index % STATUS_COLORS.length]}
                                    >
                                        <span>{item.label}</span>
                                        <span>{item.count ?? '-'}</span>
                                    </StatusItem>
                                ))}
                            </StatusesGrid>
                        </Section>
                    )}
                </DetailCard>
            )}
        </Page>
    );
};
